import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import type { QueueReceiptState } from '../../../../types/azureStorage';
import { decodeQueueMessageText } from '../../../../core/engines/azureStorageQueueCodec';
import { queueReceiptState } from '../../../../types/azureStorage';
import type { QueueMessageRow, QueueMessageSet } from './queueMessagesStore';
import { formatStorageDateTime } from '../utils/storageDateTime';

const column = (name: string, dataType = 'nvarchar'): TableExplorerColumn => ({
  name, dataType, nullable: true, isPrimaryKey: false, isForeignKey: false,
});

// UI review F16: the message body comes FIRST. Real message IDs are 36-character GUIDs and two timestamps sit beside
// them, so with Text last the body started off screen at 1200 px; the ID (a GUID nobody reads first) goes last.
const TEXT = column('Text');
// The time columns carry Azure's exact ISO instants; only their display is formatted (`queueMessageDisplayText`).
// Last UI/UX pass (Codex Z10 / Opus F7): typed as what they are — date-times and short IDs — so the shared grid never
// treats them as long text (an expand slot, and a viewer with JSON tabs for a timestamp). Only the body stays long text.
const MESSAGE_FACTS = [column('Dequeue count', 'int'), column('Inserted', 'datetime'), column('Expires', 'datetime'), column('Message ID', 'nvarchar(36)')];
const PEEK_COLUMNS = [TEXT, ...MESSAGE_FACTS];
// A received row's receipt state and visibility deadline decide what can still be done with it: right after the body.
const RECEIVED_COLUMNS = [TEXT, column('Receipt', 'nvarchar(16)'), column('Visible again', 'datetime'), ...MESSAGE_FACTS];

export function queueMessageColumns(messageSet: QueueMessageSet): TableExplorerColumn[] {
  return messageSet === 'received' ? RECEIVED_COLUMNS : PEEK_COLUMNS;
}

const receiptLabel = (state: QueueReceiptState): string => state === 'lost' ? 'Receive again' : state;

/**
 * Final UI/UX batch C1 (R14 / N11) + review C1-04: the grid shows its times in the ONE storage format
 * (`formatStorageDateTime`, as the received bar above it and the blob browser do) through the grid's presentation-only
 * `displayText` host seam (C2 N11, the entity grid's pattern). The row data keeps Azure's exact ISO instant, so the
 * cell's hover title, the grid's own Ctrl+C / Copy value and the toolbar Copy / Export all return it.
 */
const TIME_COLUMNS: ReadonlySet<string> = new Set(['Inserted', 'Expires', 'Visible again']);

export function queueMessageDisplayText(value: unknown, columnName: string): string | undefined {
  return TIME_COLUMNS.has(columnName) && typeof value === 'string' && value !== '' ? formatStorageDateTime(value) : undefined;
}

export function queueMessageRows(rows: QueueMessageRow[], decodeBase64: boolean, now: number): Record<string, unknown>[] {
  return rows.map((row) => {
    const decoded = decodeQueueMessageText(row.text, decodeBase64);
    const record: Record<string, unknown> = {
      'Message ID': row.messageId,
      Inserted: row.insertedOn,
      Expires: row.expiresOn,
      'Dequeue count': row.dequeueCount,
      Text: decoded.invalid ? `${row.text} (not Base64)` : decoded.text,
      messageId: row.messageId,
      text: row.text,
    };
    if (row.receiptHandle && row.visibleUntil) {
      // Azure's own time when it reported one; the Receipt state uses the local deadline (review Fable F9).
      record['Visible again'] = row.nextVisibleOn ?? row.visibleUntil;
      record.Receipt = receiptLabel(queueReceiptState(row as { visibleUntil: string; expiresOn?: string; lost?: boolean }, now));
      record.receiptHandle = row.receiptHandle;
      record.visibleUntil = row.visibleUntil;
      record.lost = row.lost;
    }
    return record;
  });
}

export function queueMessageExportRows(rows: QueueMessageRow[], messageSet: QueueMessageSet, decodeBase64: boolean, now: number): Record<string, unknown>[] {
  const names = queueMessageColumns(messageSet).map((item) => item.name);
  return queueMessageRows(rows, decodeBase64, now).map((row) => Object.fromEntries(names.map((name) => [name, row[name]])));
}
